import { useMemo } from "react"
import { FEATURE_FLAG } from "@constants/const/feature-flag"
import { useRisebinar, useSchools } from "@hooks/data"
import { useCourseCategory } from "@lib/hooks/data/useCourse"
import { useDigitalProducts } from "@lib/hooks/data/useDigitalProducts"

export type ProgramSectionItem = {
  slug: string
  name: string
}

export type ProgramSection = {
  key: string
  title: string
  description: string
  enabled: boolean
  items: ProgramSectionItem[]
  buildLink: (slug: string) => string
}

function buildProgramSections({
  bootcamps,
  proClasses,
  risebinars,
  categories,
  products,
}: {
  bootcamps: ReturnType<typeof useSchools>["data"]
  proClasses: ReturnType<typeof useSchools>["data"]
  risebinars: ReturnType<typeof useRisebinar>["data"]
  categories: ReturnType<typeof useCourseCategory>["data"]
  products: ReturnType<typeof useDigitalProducts>["data"]
}): ProgramSection[] {
  return [
      {
        key: "bootcamp",
        title: "Bootcamp",
        description:
          "Belajar intensif dengan career coaching dan job-guarantee, hanya dalam 3-6 bulan.",
        enabled: FEATURE_FLAG.bootcamp,
        items:
          bootcamps?.map((value) => ({
            slug: value.product_slug,
            name: value.product_name,
          })) ?? [],
        buildLink: (slug) => `/school/bootcamp/${slug}`,
      },
      {
        key: "proclass",
        title: "ProClass",
        description:
          "Kuasai skill profesional dan raih karier impian dalam waktu singkat, hanya dalam 1-2 bulan.",
        enabled: FEATURE_FLAG.proclass,
        items:
          proClasses?.map((value) => ({
            slug: value.product_slug,
            name: value.product_name,
          })) ?? [],
        buildLink: (slug) => `/school/proclass/${slug}`,
      },
      {
        key: "video_course",
        title: "Video Course",
        description:
          "Belajar bersama tutor profesional dengan kurikulum berbasis industri di mana pun, kapan pun.",
        enabled: FEATURE_FLAG.video_course,
        items:
          categories?.map((value) => ({
            slug: value.category_slug,
            name: value.category_name,
          })) ?? [],
        buildLink: (slug) => `/learning/category/${slug}`,
      },
      {
        key: "risebinar",
        title: "Event & Workshop",
        description: "Upgrade skill dan kejar karier impian dengan event & workshop berkualitas.",
        enabled: FEATURE_FLAG.risebinar,
        items:
          risebinars?.map((value) => ({
            slug: value.risebinar_slug,
            name: value.risebinar_title,
          })) ?? [],
        buildLink: (slug) => `/risebinar/${slug}`,
      },
      {
        key: "digital_product",
        title: "Digital Product",
        description:
          "Upskilling jadi mudah dan fleksibel dengan e-book, video course, dan template yang bisa diakses selamanya.",
        enabled: FEATURE_FLAG.digital_product,
        items:
          products?.map((value) => ({
            slug: value.course_slug,
            name: value.course_name,
          })) ?? [],
        buildLink: (slug) => `/digital-product/p/${slug}`,
      },
    ]
}

export function useProgramSections() {
  const { data: bootcamps, isLoading: bootcampLoading } = useSchools("BOOTCAMP")
  const { data: proClasses, isLoading: proClassLoading } = useSchools("PRO_CLASS")
  const { data: risebinars, isLoading: risebinarLoading } = useRisebinar()
  const { data: categories, isLoading: categoryLoading } = useCourseCategory()
  const { data: products, isLoading: productLoading } = useDigitalProducts()

  const sections = useMemo(
    () =>
      buildProgramSections({
        bootcamps,
        proClasses,
        risebinars,
        categories,
        products,
      }),
    [bootcamps, proClasses, categories, risebinars, products]
  )

  const visibleSections = useMemo(
    () => sections.filter((section) => section.enabled && section.items.length > 0),
    [sections]
  )

  const hasEnabledProgramSection = useMemo(
    () => sections.some((section) => section.enabled),
    [sections]
  )

  const isLoading = useMemo(() => {
    const pending: boolean[] = []
    if (FEATURE_FLAG.bootcamp) pending.push(bootcampLoading)
    if (FEATURE_FLAG.proclass) pending.push(proClassLoading)
    if (FEATURE_FLAG.video_course) pending.push(categoryLoading)
    if (FEATURE_FLAG.risebinar) pending.push(risebinarLoading)
    if (FEATURE_FLAG.digital_product) pending.push(productLoading)
    return pending.some(Boolean)
  }, [
    bootcampLoading,
    proClassLoading,
    categoryLoading,
    risebinarLoading,
    productLoading,
  ])

  const showProgramNav =
    hasEnabledProgramSection && (isLoading || visibleSections.length > 0)

  return { visibleSections, isLoading, showProgramNav }
}
